import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BedDouble, Bath, Car, Ruler, MapPin } from "lucide-react";
import { formatPrice } from "@/lib/format";

const FALLBACK = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80";

export const PropertyCard = ({ property, index = 0 }) => {
  const img = property.images?.[0] || FALLBACK;
  const specs = [
    property.bedrooms > 0 && { icon: BedDouble, label: `${property.bedrooms} Beds` },
    property.bathrooms > 0 && { icon: Bath, label: `${property.bathrooms} Baths` },
    property.garages > 0 && { icon: Car, label: `${property.garages} Parking` },
    property.area_sqm > 0 && { icon: Ruler, label: `${property.area_sqm} sqm` },
  ].filter(Boolean);

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/property/${property.id}`}
        data-testid={`property-card-${property.id}`}
        className="group block bg-white border hairline overflow-hidden hover:shadow-[0_24px_60px_-24px_rgba(20,20,20,0.25)] transition-shadow duration-500"
      >
        <div className="relative overflow-hidden aspect-[4/3]">
          <img
            src={img}
            alt={property.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 ring-1 ring-inset ring-black/10" />
          <div className="absolute left-4 top-4 flex gap-2">
            <span
              data-testid={`property-status-${property.id}`}
              className={`px-3 py-1 text-[11px] font-mono uppercase tracking-[0.15em] font-bold ${
                property.status === "rent" ? "bg-brand-emerald text-ivory" : "bg-brand text-ivory"
              }`}
            >
              {property.status === "rent" ? "For Rent" : "For Sale"}
            </span>
            {property.featured && (
              <span className="px-3 py-1 text-[11px] font-mono uppercase tracking-[0.15em] font-bold bg-brand-gold text-obsidian">
                Featured
              </span>
            )}
          </div>
          <div className="absolute bottom-4 left-4 bg-obsidian/85 backdrop-blur px-4 py-2">
            <span className="text-ivory font-display font-semibold text-base">{formatPrice(property.price)}</span>
            {property.price_label && property.status === "rent" && (
              <span className="text-ivory/60 text-xs ml-1">{property.price_label}</span>
            )}
          </div>
        </div>
        <div className="p-6">
          <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-brand font-bold">
            {property.property_type} · {property.city}
          </span>
          <h3 className="mt-2 font-serif text-xl sm:text-2xl leading-snug text-foreground group-hover:text-brand transition-colors duration-300">
            {property.title}
          </h3>
          <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-brand" />
            <span className="truncate">{property.location}</span>
          </p>
          {specs.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 border-t hairline pt-4">
              {specs.map(({ icon: Icon, label }) => (
                <span key={label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Icon className="h-4 w-4 text-brand" /> {label}
                </span>
              ))}
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
};
